import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Space,
  Input,
  Select,
  Tag,
  Row,
  Col,
  Statistic,
  message,
  Popconfirm,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  CarOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';

const { Option } = Select;

const VanList = () => {
  const navigate = useNavigate();
  const [vans, setVans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalVans: 0,
    inUseVans: 0,
    maintenanceVans: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    status: '',
  });

  const fetchVans = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        search: filters.search,
        status: filters.status,
      };
      const response = await apiService.vans.getAll(params);
      const vansData = response.data || [];
      setVans(Array.isArray(vansData) ? vansData : []);
      
      // Calculate statistics
      const totalVans = vansData.length;
      const inUseVans = vansData.filter(van => van.status === 'in-use').length;
      const maintenanceVans = vansData.filter(van => van.status === 'maintenance').length;
      const notAvailableVans = vansData.filter(van => van.status === 'not available').length;
      
      setStats({
        totalVans,
        inUseVans,
        maintenanceVans,
        notAvailableVans,
      });
    } catch (error) {
      console.error('Error fetching vans:', error);
      setError(error.response?.data?.message || 'Failed to fetch vans');
      message.error('Failed to fetch vans');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchVans();
  }, [fetchVans]);

  const handleDelete = async (id) => {
    try {
      await apiService.vans.delete(id);
      message.success('Van deleted successfully');
      fetchVans();
    } catch (error) {
      console.error('Error deleting van:', error);
      message.error('Failed to delete van');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'in-use':
        return 'processing';
      case 'maintenance':
        return 'warning';
      case 'not available':
        return 'error';
      default:
        return 'default';
    }
  };

  const columns = [
    {
      title: 'Reference',
      dataIndex: 'registration',
      key: 'registration',
      render: (text, record) => (
        <button
          type="button"
          style={{ background: 'none', border: 'none', color: '#1890ff', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}
          onClick={() => navigate(`/vans/${record._id}`)}
        >
          <Space>
            <CarOutlined />
            {text}
          </Space>
        </button>
      ),
    },
    {
      title: 'Make & Model',
      dataIndex: 'make',
      key: 'make',
      render: (text, record) => `${record.make} ${record.model}`,
    },
    {
      title: 'Year',
      dataIndex: 'year',
      key: 'year',
    },
    {
      title: 'Van Type',
      dataIndex: 'vanType',
      key: 'vanType',
      render: (type) => {
        if (type === 'company_van') return 'Company Van';
        if (type === 'rental') return 'Rental';
        if (type === 'finance') return 'Finance';
        if (type === 'contract_van') return 'Contract Van';
        if (type === 'external_van') return 'External Van';
        return type;
      },
    },
    {
      title: 'Current Mileage',
      dataIndex: 'currentMileage',
      key: 'currentMileage',
      render: (mileage) => mileage !== null && mileage !== undefined ? `${mileage.toLocaleString()} miles` : '-',
      sorter: (a, b) => (a.currentMileage || 0) - (b.currentMileage || 0),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/vans/edit/${record._id}`)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this van?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Vans"
              value={stats.totalVans}
              prefix={<CarOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="In Use Vans"
              value={stats.inUseVans}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="In Maintenance"
              value={stats.maintenanceVans}
              prefix={<ToolOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Not Available"
              value={stats.notAvailableVans}
              prefix={<CarOutlined />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>
      </Row>

      <Card
        style={{ marginTop: 16 }}
        title="Vans"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/vans/create')}
          >
            New Van
          </Button>
        }
      >
        <Space style={{ marginBottom: 16 }} wrap>
          <Input
            placeholder="Search vans"
            prefix={<SearchOutlined />}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            style={{ width: 200 }}
          />
          <Select
            placeholder="Status"
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="available">Available</Option>
            <Option value="in-use">In Use</Option>
            <Option value="maintenance">Maintenance</Option>
            <Option value="not available">Not Available</Option>
          </Select>
        </Space>

        <Table
          columns={columns}
          dataSource={vans}
          rowKey="_id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
          }}
        />
      </Card>
    </div>
  );
};

export default VanList; 